import { useState } from 'react';
import tw from 'tailwind-styled-components';
// @ts-ignore Lib has no type declarations
import { CakeIcon } from '@icons/CakeIcon';
import { EuroSignIcon } from '@icons/EuroSignIcon';
import { PrintIcon } from '@icons/PrintIcon';
import { ShefIcon } from '@icons/ShefIcon';
import { TimeIcon } from '@icons/TimeIcon';

import { GridItem, GridRow } from '../Grid';
import type { IsettingRateData } from '../../types/SettingRateData';
import type { ISocialMediaListComponents } from '../../types/SocialSharing';
import { DownArrow } from '../DownArrow';
import { ModalBox } from '../ModalBox';
import NextImage from '../NextImage';
import { PlainButton } from '../PlainButton';
import { Rating } from '../Rating';
import { ShareButton } from '../ShareButton';
import { Text as Paragraph, Title } from '../Typography';
import {
  CenterBlock,
  Container,
  DecoratedLine,
  Dots,
  DownArrowWrapper,
  ImageFirst,
  ImageSecond,
  InfoBlock,
  InfoText,
  InnerBannerContainer,
  InnerContainerStyle,
  MainContainerStyle,
  Play,
  ShareBlock,
  StyledLink,
  Tags,
} from './styles';
import { ImagesService } from 'service/images/imagesService';
import { NextLink } from 'components/NextLink';

interface ITags {
  id: string;
  title: string;
  url: string;
}
interface IRecipeOverview {
  title: string;
  rating?: {
    amount: number;
    value: number;
  };
  tags?: ITags[];
  servings: string;
  cost?: number | null;
  difficulty: number;
  preparationTime: string;
  intro?: string;
  summary: string;
  ratingMsg?: any;
  sharableImage?: string;
}
interface BannerImages {
  texture?: Record<string, string>;
  firstObject?: Record<string, string>;
  firstObjectName: string;
  secondObject?: Record<string, string>;
  secondObjectName: string;
}
const MainTitle = tw(Title)`
  mb-5
  text-center
  md:mb-8
`;

const Ratings = tw.div`
  flex
  justify-center
  align-center
  leading-none
  mb-1
  z-auto
  relative
`;

const Tag = tw(Paragraph)`
  flex
  gap-x-2
  items-center
  whitespace-nowrap
  border
  rounded-2xl
  border-current
  mb-4
  px-4
  h-8
  mx-1.5
  text-gray-dark
  hover:bg-gray
  cursor-pointer
`;

const Info = tw(Paragraph)`
  whitespace-nowrap
  mx-1.5
  flex
`;

const More = tw(Paragraph)`
  text-base
  uppercase
  font-neutral
  my-3
  mx-1
  text-center
`;

export const RecipeOverview = ({
  data,
  titles,
  copy,
  banner,
  bannerImage,
  socialMediaListComponents,
  settingRateData,
  videoId = '',
  trackingEvents,
  showCommentField = false,
  showContactPageData = false,
  contactPageData = '',
  description,
  thumbnailUrls,
  embedUrl,
  uploadDate,
  duration,
  interactionCount,
  metaTitle,
  metaDescription,
}: {
  data?: IRecipeOverview;
  titles: any;
  copy: any;
  banner?: boolean;
  bannerImage?: BannerImages;
  socialMediaListComponents?: ISocialMediaListComponents[];
  settingRateData?: IsettingRateData;
  videoId?: string;
  trackingEvents?: {
    clickSocialShare: (
      eventName: string,
      eventEC: string,
      socialPlatform: string,
      sharedItemName: string,
    ) => void;
    clickRatingRecipe: (value: number, title: string) => void;
    clickPrintRecipe: (title: string) => void;
  };
  showCommentField?: boolean;
  showContactPageData?: boolean;
  contactPageData?: string;
  thumbnailUrls?: string[];
  embedUrl?: string;
  uploadDate?: string;
  duration?: string;
  interactionCount?: number;
  description?: string;
  metaTitle?: string;
  metaDescription?: string;
}) => {
  const TextLimit = 297;
  const trimSummary = data?.summary.substring(0, TextLimit);
  const [expend, setTest] = useState(true);
  const clickHandler = () => {
    setTest(!expend);
  };
  const readMoreCopy = titles?.readMore || 'Read More';
  const readLessCopy = titles?.readLess || 'Read Less';

  const RecipeOverviewTemplate = (
    ratingcopy: any,
    title: any,
    settingRatesData: any,
    recipedata = data,
  ) => {
    const getTagIcon = (id: string) => {
      if (id === 'Vegetarian') {
        return (
          <svg
            width="18"
            height="16"
            viewBox="0 0 18 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1.49853 4.42097C1.39634 3.83017 1.35226 3.29633 1.33503 2.88483C1.74653 2.90206 2.28036 2.94614 2.87117 3.04833C4.23322 3.28392 5.79039 3.80968 6.89648 4.91578C8.00258 6.02187 8.52834 7.57904 8.76393 8.94109C8.86612 9.5319 8.91019 10.0657 8.92743 10.4772C8.51592 10.46 7.98209 10.4159 7.39128 10.3137C6.02924 10.0781 4.47207 9.55238 3.36597 8.44629C2.25988 7.34019 1.73412 5.78302 1.49853 4.42097Z"
              stroke="#2D2A29"
              strokeWidth="1.75"
            />
            <path
              d="M10.6212 8.31373C10.0304 8.41592 9.49653 8.46 9.08503 8.47723C9.10226 8.06573 9.14634 7.5319 9.24853 6.94109C9.48412 5.57904 10.0099 4.02187 11.116 2.91578C12.2221 1.80968 13.7792 1.28392 15.1413 1.04833C15.7321 0.946142 16.2659 0.902066 16.6774 0.884833C16.6602 1.29633 16.6161 1.83017 16.5139 2.42098C16.2783 3.78302 15.7526 5.34019 14.6465 6.44629C13.5404 7.55238 11.9832 8.07814 10.6212 8.31373Z"
              stroke="#2D2A29"
              strokeWidth="1.75"
            />
            <rect x="8.2002" y="8" width="1.7" height="8" rx="0.85" fill="#2D2A29" />
          </svg>
        );
      }
      if (id === 'Vegan') {
        return (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.139 19.383L11.1378 19.3818C8.54557 17.0312 6.46449 15.1395 5.02123 13.3737C3.58759 11.6198 2.875 10.0973 2.875 8.5C2.875 5.89429 4.90221 3.875 7.5 3.875C8.97548 3.875 10.4061 4.5664 11.336 5.64987L12 6.4235L12.664 5.64987C13.5939 4.5664 15.0245 3.875 16.5 3.875C19.0978 3.875 21.125 5.89429 21.125 8.5C21.125 10.0973 20.4124 11.6198 18.9788 13.3737C17.5355 15.1395 15.4544 17.0312 12.8622 19.3818L12.861 19.383L12 20.1667L11.139 19.383Z"
              stroke="#2D2A29"
              strokeWidth="1.75"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M9.26316 8H7.5L11.3684 15.5H12.6316L16.5 8H14.7368L12 14.0937L9.26316 8Z"
              fill="#2D2A29"
            />
          </svg>
        );
      }
      if (id === 'Gluten-free') {
        return (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 3.53125C7.18359 3.53125 3.28125 7.43359 3.28125 12.25C3.28125 17.0664 7.18359 20.9688 12 20.9688C16.8164 20.9688 20.7188 17.0664 20.7188 12.25C20.7188 7.43359 16.8164 3.53125 12 3.53125ZM12 19.2812C8.09766 19.2812 4.96875 16.1523 4.96875 12.25C4.96875 8.38281 8.09766 5.21875 12 5.21875C15.8672 5.21875 19.0312 8.38281 19.0312 12.25C19.0312 16.1523 15.8672 19.2812 12 19.2812Z"
              fill="#2D2A29"
            />
            <mask
              id="mask0_15219_362263"
              style={{ maskType: 'alpha' }}
              maskUnits="userSpaceOnUse"
              x="4"
              y="4"
              width="16"
              height="16"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20ZM12 17C14.7614 17 17 14.7614 17 12C17 9.23858 14.7614 7 12 7C9.23858 7 7 9.23858 7 12C7 14.7614 9.23858 17 12 17Z"
                fill="white"
              />
            </mask>
            <g mask="url(#mask0_15219_362263)">
              <rect
                x="6.09961"
                y="17"
                width="15.0243"
                height="1.65"
                transform="rotate(-45 6.09961 17)"
                fill="#2D2A29"
              />
            </g>
            <rect
              x="11.6228"
              y="10.0234"
              width="0.750254"
              height="7.50254"
              rx="0.375127"
              fill="#2D2A29"
            />
            <path
              d="M12.7512 16.0228C12.7512 16.0228 13.8271 16.0931 14.4473 15.4729C15.0675 14.8527 14.9971 13.7768 14.9971 13.7768C14.9971 13.7768 13.9212 13.7065 13.301 14.3267C12.6808 14.9469 12.7512 16.0228 12.7512 16.0228Z"
              fill="#2D2A29"
            />
            <path
              d="M11.2454 16.0228C11.2454 16.0228 10.1695 16.0931 9.54928 15.4729C8.92908 14.8527 8.99945 13.7768 8.99945 13.7768C8.99945 13.7768 10.0754 13.7065 10.6956 14.3267C11.3158 14.9469 11.2454 16.0228 11.2454 16.0228Z"
              fill="#2D2A29"
            />
            <path
              d="M12.7512 13.3973C12.7512 13.3973 13.8271 13.4676 14.4473 12.8474C15.0675 12.2272 14.9971 11.1513 14.9971 11.1513C14.9971 11.1513 13.9212 11.081 13.301 11.7012C12.6808 12.3214 12.7512 13.3973 12.7512 13.3973Z"
              fill="#2D2A29"
            />
            <path
              d="M11.2454 13.3973C11.2454 13.3973 10.1695 13.4676 9.54928 12.8474C8.92908 12.2272 8.99945 11.1513 8.99945 11.1513C8.99945 11.1513 10.0754 11.081 10.6956 11.7012C11.3158 12.3214 11.2454 13.3973 11.2454 13.3973Z"
              fill="#2D2A29"
            />
            <path
              d="M12.7512 10.7708C12.7512 10.7708 13.8271 10.8412 14.4473 10.221C15.0675 9.60078 14.9971 8.52487 14.9971 8.52487C14.9971 8.52487 13.9212 8.4545 13.301 9.0747C12.6808 9.69491 12.7512 10.7708 12.7512 10.7708Z"
              fill="#2D2A29"
            />
            <path
              d="M11.9981 9.65086C11.9981 9.65086 12.7483 8.93983 12.7483 8.06273C12.7483 7.18564 11.9981 6.47461 11.9981 6.47461C11.9981 6.47461 11.2478 7.18564 11.2478 8.06273C11.2478 8.93983 11.9981 9.65086 11.9981 9.65086Z"
              fill="#2D2A29"
            />
            <path
              d="M11.2454 10.7708C11.2454 10.7708 10.1695 10.8412 9.54928 10.221C8.92908 9.60078 8.99945 8.52487 8.99945 8.52487C8.99945 8.52487 10.0754 8.4545 10.6956 9.0747C11.3158 9.69491 11.2454 10.7708 11.2454 10.7708Z"
              fill="#2D2A29"
            />
          </svg>
        );
      }
      if (id === 'Lactose-free') {
        return (
          <svg
            width="25"
            height="24"
            viewBox="0 0 25 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12.5 3.53125C7.68359 3.53125 3.78125 7.43359 3.78125 12.25C3.78125 17.0664 7.68359 20.9688 12.5 20.9688C17.3164 20.9688 21.2188 17.0664 21.2188 12.25C21.2188 7.43359 17.3164 3.53125 12.5 3.53125ZM12.5 19.2812C8.59766 19.2812 5.46875 16.1523 5.46875 12.25C5.46875 8.38281 8.59766 5.21875 12.5 5.21875C16.3672 5.21875 19.5312 8.38281 19.5312 12.25C19.5312 16.1523 16.3672 19.2812 12.5 19.2812Z"
              fill="#2D2A29"
            />
            <mask
              id="mask0_15219_362299"
              style={{ maskType: 'alpha' }}
              maskUnits="userSpaceOnUse"
              x="4"
              y="4"
              width="17"
              height="16"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12.5 20C16.9183 20 20.5 16.4183 20.5 12C20.5 7.58172 16.9183 4 12.5 4C8.08172 4 4.5 7.58172 4.5 12C4.5 16.4183 8.08172 20 12.5 20ZM11 7C9.89543 7 9 7.89543 9 9V16C9 17.1046 9.89543 18 11 18H14C15.1046 18 16 17.1046 16 16V10C16 8.34315 14.6569 7 13 7H11Z"
                fill="white"
              />
            </mask>
            <g mask="url(#mask0_15219_362299)">
              <rect
                x="6.59961"
                y="17"
                width="15.0243"
                height="1.65"
                transform="rotate(-45 6.59961 17)"
                fill="#2D2A29"
              />
            </g>
            <path
              d="M13.9999 9L14.3353 8.83227L13.9999 9ZM13.5 7L13.875 6.99991L13.8749 6.625H13.5V7ZM11.5 7V6.625H11.1251L11.125 6.99989L11.5 7ZM11.5 7.00122L11.125 7.00111V7.00113L11.5 7.00122ZM11.0001 9L10.6647 8.83227L11.0001 9ZM13.6645 9.16773C14.1289 10.0964 14.125 10.6987 14.125 11H14.875C14.875 10.6347 14.8711 9.9036 14.3353 8.83227L13.6645 9.16773ZM13.125 7.00009C13.1251 7.25048 13.1244 7.53366 13.1949 7.88617C13.2653 8.23848 13.4033 8.64541 13.6645 9.16773L14.3353 8.83227C14.0968 8.35541 13.9849 8.01213 13.9304 7.73914C13.8758 7.46634 13.8751 7.24911 13.875 6.99991L13.125 7.00009ZM12.5 7.375H13.5V6.625H12.5V7.375ZM11.5 7.375H12.5V6.625H11.5V7.375ZM11.875 7.00133L11.875 7.00011L11.125 6.99989L11.125 7.00111L11.875 7.00133ZM11.3355 9.16773C11.5966 8.64547 11.7346 8.23888 11.8051 7.88685C11.8756 7.53461 11.8749 7.2517 11.875 7.00132L11.125 7.00113C11.1249 7.25034 11.1242 7.46723 11.0697 7.73968C11.0151 8.01235 10.9032 8.35534 10.6647 8.83227L11.3355 9.16773ZM10.875 11C10.875 10.6987 10.8711 10.0964 11.3355 9.16773L10.6647 8.83227C10.1289 9.9036 10.125 10.6347 10.125 11H10.875ZM10.875 16V11H10.125V16H10.875ZM11.5 16.625C11.1548 16.625 10.875 16.3452 10.875 16H10.125C10.125 16.7594 10.7406 17.375 11.5 17.375V16.625ZM13.5 16.625H11.5V17.375H13.5V16.625ZM14.125 16C14.125 16.3452 13.8452 16.625 13.5 16.625V17.375C14.2594 17.375 14.875 16.7594 14.875 16H14.125ZM14.125 11V16H14.875V11H14.125Z"
              fill="#2D2A29"
            />
            <path d="M14 7H11" stroke="#2D2A29" strokeWidth="0.75" strokeLinecap="round" />
            <path
              d="M14.5 11.8081C14.1667 11.6194 13.3 11.3553 12.5 11.8081C11.7 12.2609 10.8333 11.9968 10.5 11.8081"
              stroke="#2D2A29"
              strokeWidth="0.75"
              strokeLinecap="round"
            />
          </svg>
        );
      }
      if (id.includes('Low') || id.includes('Less')) {
        return (
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9 0.53125C4.18359 0.53125 0.28125 4.43359 0.28125 9.25C0.28125 14.0664 4.18359 17.9688 9 17.9688C13.8164 17.9688 17.7188 14.0664 17.7188 9.25C17.7188 4.43359 13.8164 0.53125 9 0.53125ZM9 16.2812C5.09766 16.2812 1.96875 13.1523 1.96875 9.25C1.96875 5.38281 5.09766 2.21875 9 2.21875C12.8672 2.21875 16.0312 5.38281 16.0312 9.25C16.0312 13.1523 12.8672 16.2812 9 16.2812Z"
              fill="#2D2A29"
            />
            <path
              d="M12.0651 9.32704L9.61462 11.6887L9.61462 5.1107C9.61462 4.87704 9.4158 4.65 9.14035 4.65L8.82846 4.65C8.70365 4.65 8.58632 4.70922 8.50176 4.79072C8.41743 4.87199 8.35419 4.98668 8.35419 5.1107L8.35419 11.6867L5.93532 9.32745L5.93579 9.32697L5.92656 9.31985C5.73463 9.17187 5.46615 9.17187 5.27422 9.31985L5.2672 9.32526L5.26125 9.33182L5.04846 9.56619C4.84964 9.72698 4.85052 10.0364 5.05108 10.1959L8.66519 13.709L8.6655 13.7093C8.86018 13.8969 9.13982 13.8969 9.3345 13.7093L9.33481 13.709L12.9489 10.1959C13.1495 10.0364 13.1504 9.72698 12.9515 9.56619L12.7388 9.33182L12.7328 9.32526L12.7258 9.31984C12.5339 9.17187 12.2654 9.17187 12.0734 9.31984L12.0731 9.31936L12.0651 9.32704Z"
              fill="#2D2A29"
              stroke="#2D2A29"
              strokeWidth="0.2"
            />
          </svg>
        );
      }
      return (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 3.53125C7.18359 3.53125 3.28125 7.43359 3.28125 12.25C3.28125 17.0664 7.18359 20.9688 12 20.9688C16.8164 20.9688 20.7188 17.0664 20.7188 12.25C20.7188 7.43359 16.8164 3.53125 12 3.53125ZM12 19.2812C8.09766 19.2812 4.96875 16.1523 4.96875 12.25C4.96875 8.38281 8.09766 5.21875 12 5.21875C15.8672 5.21875 19.0312 8.38281 19.0312 12.25C19.0312 16.1523 15.8672 19.2812 12 19.2812Z"
            fill="#2D2A29"
          />
          <rect
            x="6.09961"
            y="17"
            width="15.0243"
            height="1.65"
            transform="rotate(-45 6.09961 17)"
            fill="#2D2A29"
          />
        </svg>
      );
    };
    return (
      <>
        <Ratings data-test="recipe-overview-ratings" data-print="hide">
          <Rating
            data-test="recipe-overivew-rating"
            ratingValue={recipedata?.rating?.value}
            ratingTotal={recipedata?.rating?.amount || 0}
            ratingData={ratingcopy}
            ratingMsg={recipedata?.ratingMsg}
            settingRateData={settingRatesData}
            // eslint-disable-next-line react/prop-types
            trackingEvent={trackingEvents?.clickRatingRecipe && trackingEvents.clickRatingRecipe}
            recipeTitle={recipedata?.title || ''}
            showCommentField={showCommentField}
            showContactPageData={showContactPageData}
            contactPageData={contactPageData}
          />
          {recipedata?.rating && recipedata?.rating?.value > 0 && (
            <div itemProp="aggregateRating" itemScope itemType="http://schema.org/AggregateRating">
              <span className="hidden" itemProp="ratingValue">
                {recipedata?.rating?.value?.toFixed(0)}
              </span>
              <span className="hidden" itemProp="ratingCount">
                {recipedata?.rating?.amount}
              </span>
              <span className="hidden" itemProp="bestRating">
                5
              </span>
            </div>
          )}
        </Ratings>

        <MainTitle data-test="recipe-overview-main-title" itemProp="name" tag="h1" type="lg">
          {recipedata?.title}
        </MainTitle>
        <InfoBlock data-test="recipe-overview-info-block" data-print="margin-1-rem">
          {recipedata?.cost !== undefined && !!title?.cost[recipedata?.cost as number] && (
            <>
              <Info tag="div" type="lead">
                <EuroSignIcon />
                <InfoText data-test="recipe-overview-info-text-title">
                  {title?.cost && title?.cost?.[recipedata?.cost as number]}
                </InfoText>
              </Info>
              {!!recipedata?.servings && <Dots className="inline-block" />}
            </>
          )}
          {recipedata?.servings ? (
            <>
              <Info tag="div" type="lead" itemProp="recipeYield">
                <CakeIcon />
                <InfoText data-test="recipe-overview-info-text-servings">
                  {recipedata?.servings}
                </InfoText>
              </Info>
              {recipedata?.difficulty !== undefined && <Dots className="inline-block" />}
            </>
          ) : (
            recipedata?.difficulty !== undefined &&
            recipedata?.cost !== undefined &&
            !!title?.cost[recipedata?.cost as number] && <Dots className="inline-block" />
          )}
          {recipedata?.difficulty !== undefined ? (
            <>
              <Info tag="div" type="lead">
                <ShefIcon />
                <InfoText data-test="recipe-overview-info-text-difficulty">
                  {/* eslint-disable-next-line react/prop-types */}
                  {titles.difficulty[recipedata.difficulty]}
                </InfoText>
              </Info>
              {recipedata?.preparationTime !== '' && <Dots className="inline-block" />}
            </>
          ) : (
            recipedata?.servings !== undefined &&
            recipedata?.preparationTime !== '' && <Dots className="inline-block" />
          )}
          {recipedata?.preparationTime !== '' && (
            <Info tag="div" type="lead">
              <TimeIcon />
              <InfoText data-test="recipe-overview-info-text-prep-time">
                {recipedata?.preparationTime}
              </InfoText>
            </Info>
          )}
        </InfoBlock>
        <Tags data-test="recipe-overview-main-tags" data-print="hide">
          {recipedata?.tags?.map(
            (tag, index) =>
              tag && (
                // eslint-disable-next-line react/no-array-index-key
                <StyledLink key={tag.id ?? `tag-${index}`}>
                  <NextLink href={tag.url} target="_blank" rel="noopener noreferrer">
                    <Tag data-test="recipe-overview-tag" tag="div" type="lead">
                      {tag.id && getTagIcon(tag.id)}
                      {tag.title}
                    </Tag>
                  </NextLink>
                </StyledLink>
              ),
          )}
        </Tags>
        {banner && videoId !== '' && (
          <div className="mx-auto inline-block w-auto">
            <Play>
              <ModalBox
                data-test="video-modal-box"
                videoID={videoId}
                description={description || ''}
                thumbnailUrls={thumbnailUrls ?? []}
                embedUrl={embedUrl || ''}
                uploadDate={uploadDate || ''}
                duration={duration || ''}
                interactionCount={interactionCount || 0}
              />
            </Play>
            <Paragraph tag="span" type="lead" className="inline-block">
              {ratingcopy.videoLabel}
            </Paragraph>
          </div>
        )}
      </>
    );
  };
  return (
    <Container data-test="recipe-overview-container" banner data-print="margin-1-rem">
      {banner ? (
        <div className={MainContainerStyle}>
          {bannerImage && bannerImage.secondObject && (
            <div>
              <ImageFirst>
                <NextImage
                  data-test="recipe-overview-image-first"
                  sources={ImagesService.getPictureSources(bannerImage?.firstObject)}
                  alt={bannerImage?.firstObjectName || ''}
                  mimeType={String(bannerImage?.firstObject?.mimeType)}
                />
              </ImageFirst>
            </div>
          )}
          <InnerBannerContainer
            data-test="recipe-overview-inner-banner"
            className={InnerContainerStyle}
            backgroundImg={bannerImage?.texture?.background || ''}
          >
            {RecipeOverviewTemplate(copy, titles, settingRateData, data)}
          </InnerBannerContainer>
          {bannerImage && bannerImage.secondObject && (
            <ImageSecond>
              <NextImage
                data-test="recipe-overview-image-second"
                sources={ImagesService.getPictureSources(bannerImage?.secondObject)}
                alt={bannerImage?.secondObjectName || ''}
              />
            </ImageSecond>
          )}
        </div>
      ) : (
        RecipeOverviewTemplate(copy, titles, settingRateData, data)
      )}
      {banner && (
        <DownArrowWrapper>
          <DownArrow data-test="recipe-overview-down-arrow" />
        </DownArrowWrapper>
      )}
      <GridRow columns={16}>
        <GridItem colSpan={2} />
        <GridItem colSpan={12}>
          {/* Short Description from Recipe Database */}
          {data?.intro && (
            <Paragraph
              tag="div"
              type="lg"
              data-test="recipe-overview-description"
              className="text-center mb-2 tracking-sm"
              id="recipe-details"
              itemProp="description"
              data-print="show"
            >
              {data?.intro}
            </Paragraph>
          )}
          {/* Intro Text from Recipe Database */}
          {data?.summary && (
            <>
              <Paragraph
                tag="div"
                type="lg"
                data-test="recipe-overview-description"
                className="text-center mb-2 tracking-sm"
                id="recipe-details"
                itemProp="description"
                data-print="hide"
              >
                {data?.summary?.length >= TextLimit && expend ? `${trimSummary}...` : data?.summary}
              </Paragraph>
              {data?.summary?.length >= TextLimit && (
                <CenterBlock data-test="recipe-overview-center-block" data-print="hide">
                  <button onClick={clickHandler} data-test="more" type="button">
                    <More tag="div" type="link">
                      {expend ? `${readMoreCopy}  +` : `${readLessCopy}  -`}
                    </More>
                  </button>
                </CenterBlock>
              )}
            </>
          )}
          <ShareBlock data-test="recipe-overview-share-block" data-print="hide">
            <DecoratedLine />
            <span className="mr-3 mb-2 mmd:mb-0">
              <PlainButton
                label={titles.print}
                icon={<PrintIcon />}
                onClick={() =>
                  trackingEvents?.clickPrintRecipe &&
                  trackingEvents.clickPrintRecipe(data?.title || '')
                }
                data-test="button-print"
                data-ec="recipe"
                data-ea="print"
                data-el="print recipe"
                data-event_name="click_print_recipe"
              />
            </span>
            <ShareButton
              data-test="recipe-overview-social-sharing"
              socialMediaListComponents={socialMediaListComponents}
              shareText={data?.title}
              shareImage={data?.sharableImage}
              labels={titles}
              trackingEvents={
                trackingEvents?.clickSocialShare && {
                  clickSocialShare: trackingEvents?.clickSocialShare,
                }
              }
              metaTitle={metaTitle}
              metaDescription={metaDescription}
            />
            <DecoratedLine className="md:ml-5" />
          </ShareBlock>
        </GridItem>
        <GridItem colSpan={2} />
      </GridRow>
    </Container>
  );
};
